
<p-confirmDialog />

<div class="card">
  <div class="card-content">

    <!-- Seed defaults prompt shown only if table is empty -->
    <div *ngIf="!loading && templates.length === 0" class="seed-bar">
      <p>No onboarding templates configured. Seed the default BISTEC Global onboarding tasks?</p>
      <p-button label="Seed Default Templates" icon="pi pi-download" severity="primary" (onClick)="seedDefaults()" />
    </div>

    <!-- ============ Tab bar ============ -->
    <div *ngIf="!loading && templates.length > 0" class="ot-tabbar" role="tablist">
      <button
        *ngFor="let t of tabs"
        type="button"
        role="tab"
        [attr.aria-selected]="activeTab === t.key"
        class="ot-tab"
        [class.ot-tab--active]="activeTab === t.key"
        (click)="setTab(t.key)">
        <i class="pi {{ getCategoryIcon(t.key) }} ot-tab-icon"></i>
        <span class="ot-tab-label">{{ t.label }}</span>
        <span class="ot-tab-count">{{ getTabCount(t.key) }}</span>
      </button>
    </div>

    <!-- ============ Per-tab toolbar: search + status + add ============ -->
    <div *ngIf="!loading && templates.length > 0" class="ot-toolbar">
      <div class="ot-toolbar-left">
        <span class="ot-search">
          <i class="pi pi-search"></i>
          <input pInputText type="text"
            [placeholder]="'Search ' + activeTabLabel + ' tasks…'"
            [(ngModel)]="tabSearch" (ngModelChange)="refreshDisplay()" />
        </span>
        <p-select
          [(ngModel)]="tabStatusFilter"
          (onChange)="refreshDisplay()"
          [options]="statusFilterOptions"
          optionLabel="label"
          optionValue="value"
          styleClass="ot-status-filter"
          appendTo="body" />
      </div>
      <div class="ot-toolbar-right">
        <!-- Bulk-delete button appears only when tasks are selected in the current tab -->
        <p-button
          *ngIf="selectedIds.length > 0"
          [label]="'Delete Selected (' + selectedIds.length + ')'"
          icon="pi pi-trash"
          severity="danger"
          [text]="true"
          [loading]="deletingBulk"
          (onClick)="deleteSelected()" />
        <p-button
          *ngIf="selectedIds.length > 0"
          label="Clear"
          icon="pi pi-times"
          severity="secondary"
          [text]="true"
          (onClick)="clearSelection()" />
        <p-button
          label="Manage order"
          icon="pi pi-sort-alt"
          severity="secondary"
          [outlined]="true"
          (onClick)="openManageOrder()" />
        <p-button
          [label]="'Add ' + activeTabLabel + ' Task'"
          icon="pi pi-plus"
          severity="primary"
          (onClick)="showAddDialog()" />
      </div>
    </div>

    <!-- ============ IT / L&D tabs: single reorderable table ============ -->
    <p-table
      *ngIf="!loading && templates.length > 0 && activeTab !== 'PnC'"
      [value]="displayTasks"
      [loading]="reordering"
      (onRowReorder)="onRowReorder($event)"
      [tableStyle]="{ 'min-width': '100%' }"
      styleClass="p-datatable no-column-lines ot-task-table">

      <ng-template pTemplate="header">
        <tr>
          <th style="width: 2.5rem" class="ot-drag-cell"></th>
          <th style="width: 2.5rem" class="text-center">
            <p-checkbox [binary]="true" [ngModel]="isAllVisibleSelected()"
              (ngModelChange)="toggleSelectAllVisible($event)" pTooltip="Select all visible" tooltipPosition="top" />
          </th>
          <th>Task Name</th>
          <th style="width: 160px">Email Type</th>
          <th class="th-actions" style="width: 130px">Actions</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-template let-index="rowIndex">
        <tr [pReorderableRow]="index" [class.ot-row-selected]="isSelected(template.id)"
            [class.row-inactive]="template.isDefault === false">
          <td class="ot-drag-cell">
            <span class="pi pi-bars ot-drag-handle" pReorderableRowHandle pTooltip="Drag to reorder" tooltipPosition="right"></span>
          </td>
          <td class="text-center">
            <p-checkbox [binary]="true" [ngModel]="isSelected(template.id)" (ngModelChange)="toggleSelect(template.id)" />
          </td>
          <td class="td-truncate" [pTooltip]="template.taskName" tooltipPosition="top">{{ template.taskName }}</td>
          <td>
            <p-tag *ngIf="template.emailType" icon="pi pi-envelope" [value]="getEmailTypeLabel(template.emailType)" severity="warn" style="font-size: 0.7rem" />
            <span *ngIf="!template.emailType" class="text-muted" style="font-size: 0.8rem">—</span>
          </td>
          <td class="table-cell-actions">
            <div class="table-actions-stack">
              <p-button [icon]="template.isDefault ? 'pi pi-check-circle' : 'pi pi-times-circle'"
                [severity]="template.isDefault ? 'success' : 'secondary'" [text]="true" size="small"
                [pTooltip]="template.isDefault ? 'Active — click to deactivate' : 'Inactive — click to activate'"
                tooltipPosition="top" (onClick)="toggleActive(template)" />
              <p-button icon="pi pi-pencil" [text]="true" severity="info" size="small" pTooltip="Edit" tooltipPosition="top" (onClick)="showEditDialog(template)" />
              <p-button icon="pi pi-trash" [text]="true" severity="danger" size="small" pTooltip="Delete" tooltipPosition="top" (onClick)="deleteTemplate(template)" />
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr class="table-empty-row">
          <td colspan="5" class="table-empty-cell">
            <div class="empty-state">
              <div class="empty-icon"><i class="pi pi-inbox"></i></div>
              <p class="empty-message">No {{ activeTabLabel }} tasks<ng-container *ngIf="tabSearch || tabStatusFilter !== 'all'"> match this filter</ng-container></p>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>

    <!-- ============ P&C tab: one section per sub-category, in the user-defined order ============ -->
    <div *ngIf="!loading && templates.length > 0 && activeTab === 'PnC'" class="ot-pnc-groups">
      <div *ngFor="let group of pncGroups" class="ot-pnc-group">
        <div class="ot-subcat-header-row ot-subcat-standalone">
          <span class="ot-subcat-label">
            <i class="pi {{ getCategoryIcon(group.category) }} ot-subcat-icon"></i>
            {{ getCategoryLabel(group.category) }}
          </span>
        </div>

        <p-table *ngIf="group.tasks.length > 0"
          [value]="group.tasks" [loading]="reordering"
          (onRowReorder)="onGroupRowReorder(group.category, $event)"
          [tableStyle]="{ 'min-width': '100%' }"
          styleClass="p-datatable no-column-lines ot-task-table">
          <ng-template pTemplate="body" let-template let-index="rowIndex">
            <tr [pReorderableRow]="index" [class.ot-row-selected]="isSelected(template.id)"
                [class.row-inactive]="template.isDefault === false">
              <td class="ot-drag-cell" style="width: 2.5rem">
                <span class="pi pi-bars ot-drag-handle" pReorderableRowHandle pTooltip="Drag to reorder" tooltipPosition="right"></span>
              </td>
              <td class="text-center" style="width: 2.5rem">
                <p-checkbox [binary]="true" [ngModel]="isSelected(template.id)" (ngModelChange)="toggleSelect(template.id)" />
              </td>
              <td class="td-truncate" [pTooltip]="template.taskName" tooltipPosition="top">{{ template.taskName }}</td>
              <td style="width: 160px">
                <p-tag *ngIf="template.emailType" icon="pi pi-envelope" [value]="getEmailTypeLabel(template.emailType)" severity="warn" style="font-size: 0.7rem" />
                <span *ngIf="!template.emailType" class="text-muted" style="font-size: 0.8rem">—</span>
              </td>
              <td class="table-cell-actions" style="width: 130px">
                <div class="table-actions-stack">
                  <p-button [icon]="template.isDefault ? 'pi pi-check-circle' : 'pi pi-times-circle'"
                    [severity]="template.isDefault ? 'success' : 'secondary'" [text]="true" size="small"
                    [pTooltip]="template.isDefault ? 'Active — click to deactivate' : 'Inactive — click to activate'"
                    tooltipPosition="top" (onClick)="toggleActive(template)" />
                  <p-button icon="pi pi-pencil" [text]="true" severity="info" size="small" pTooltip="Edit" tooltipPosition="top" (onClick)="showEditDialog(template)" />
                  <p-button icon="pi pi-trash" [text]="true" severity="danger" size="small" pTooltip="Delete" tooltipPosition="top" (onClick)="deleteTemplate(template)" />
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>

        <div *ngIf="group.tasks.length === 0" class="ot-group-empty">No tasks in this category</div>
      </div>
    </div>

    <!-- Loading state -->
    <div *ngIf="loading" class="ot-loading">
      <i class="pi pi-spin pi-spinner"></i>
      <span>Loading templates…</span>
    </div>
  </div>
</div>

<!-- ============ Add / Edit dialog ============ -->
<p-dialog
  [visible]="displayDialog"
  (visibleChange)="displayDialog = $event"
  [header]="isEditMode ? 'Edit Task Template' : ('Add ' + activeTabLabel + ' Task')"
  [modal]="true"
  styleClass="dialog-md"
  [dismissableMask]="true"
  [closable]="true">

  <div class="form-dialog-form">
    <div class="form-dialog-container">

      <!-- Category — hidden in add mode when the tab has only one category value -->
      <div class="form-row" *ngIf="isEditMode || dialogCategoryOptions.length > 1">
        <div class="field" [class.field-invalid]="fieldTouched['category'] && !selectedTemplate.category">
          <label for="category">Category <span class="required-star">*</span></label>
          <p-select id="category" [(ngModel)]="selectedTemplate.category" [options]="dialogCategoryOptions"
            optionLabel="label" optionValue="value" placeholder="Select category" appendTo="body"
            (onBlur)="fieldTouched['category'] = true" [filter]="true" filterPlaceholder="Search…" />
          <small class="field-error" *ngIf="fieldTouched['category'] && !selectedTemplate.category">Required</small>
        </div>
      </div>

      <div class="form-row">
        <div class="field" [class.field-invalid]="fieldTouched['taskName'] && !selectedTemplate.taskName.trim()">
          <label for="taskName">Task Name <span class="required-star">*</span></label>
          <textarea id="taskName" [(ngModel)]="selectedTemplate.taskName"
            [rows]="2" class="task-textarea"
            placeholder="e.g. Prepare Welcome Pack"
            (blur)="fieldTouched['taskName'] = true"></textarea>
          <small class="field-error" *ngIf="fieldTouched['taskName'] && !selectedTemplate.taskName.trim()">Required</small>
        </div>
      </div>

      <div class="form-row">
        <div class="field">
          <label for="emailType">Email Action</label>
          <p-select id="emailType" [(ngModel)]="selectedTemplate.emailType" [options]="emailTypeOptions"
            optionLabel="label" optionValue="value" placeholder="No email — task only" appendTo="body" />
          <small class="field-hint">When set, an email send button appears next to this task in the onboarding checklist.</small>
        </div>
      </div>

      <div class="form-row">
        <div class="field">
          <label>Status</label>
          <div class="status-toggle">
            <p-checkbox [(ngModel)]="selectedTemplate.isDefault" [binary]="true" inputId="isDefault" />
            <label for="isDefault">Active — include when onboarding starts</label>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="form-dialog-footer-btns">
      <p-button label="Cancel" icon="pi pi-times" (onClick)="hideDialog()" severity="secondary" [outlined]="true" />
      <p-button label="Save" icon="pi pi-check" (onClick)="saveTemplate()" severity="primary" />
    </div>
  </ng-template>
</p-dialog>

<!-- ============ Manage category order popup ============ -->
<p-dialog [(visible)]="manageOrderVisible" [modal]="true" [style]="{ width: '34rem' }"
          header="Manage category order" [draggable]="false" [dismissableMask]="true">
  <div class="order-popup">
    <p class="order-intro">Drag rows (or use the arrows) to reorder, and pick an icon per category.</p>

    <!-- Scope toggle: top-level tabs vs P&C sub-categories -->
    <div class="order-scope">
      <button type="button" class="order-scope-btn" [class.active]="orderScope === 'tabs'" (click)="setOrderScope('tabs')">Tabs</button>
      <button type="button" class="order-scope-btn" [class.active]="orderScope === 'pncSub'" (click)="setOrderScope('pncSub')">P&amp;C sub-categories</button>
    </div>

    <p-table [value]="orderDraft" (onRowReorder)="onOrderReorder()" styleClass="p-datatable-sm order-table">
      <ng-template pTemplate="body" let-c let-index="rowIndex">
        <tr [pReorderableRow]="index">
          <td style="width: 2.5rem;"><span class="pi pi-bars order-drag" pReorderableRowHandle></span></td>
          <td style="width: 3rem;"><i class="pi {{ c.icon || 'pi-folder' }}"></i></td>
          <td>{{ c.label }}</td>
          <td style="width: 12rem;">
            <p-select [options]="iconChoices" [(ngModel)]="c.icon" appendTo="body"
                      [style]="{ width: '100%' }" placeholder="Icon">
              <ng-template let-icon pTemplate="item"><i class="pi {{ icon }}"></i> &nbsp; {{ icon }}</ng-template>
              <ng-template let-icon pTemplate="selectedItem"><i class="pi {{ icon }}"></i> &nbsp; {{ icon }}</ng-template>
            </p-select>
          </td>
          <td style="width: 4.5rem;">
            <button type="button" class="order-move" (click)="moveOrderItem(index, -1)" [disabled]="index === 0"><i class="pi pi-chevron-up"></i></button>
            <button type="button" class="order-move" (click)="moveOrderItem(index, 1)" [disabled]="index === orderDraft.length - 1"><i class="pi pi-chevron-down"></i></button>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>
  <ng-template pTemplate="footer">
    <div class="form-dialog-footer-btns">
      <p-button label="Cancel" icon="pi pi-times" (onClick)="manageOrderVisible = false" severity="secondary" [outlined]="true" />
      <p-button label="Save order" icon="pi pi-check" [loading]="savingOrder" (onClick)="saveOrder()" severity="primary" />
    </div>
  </ng-template>
</p-dialog>
